/**
 * Cluster Template tracking API service
 *
 * Gerencia templates baixados em clusters Proxmox.
 * Fornece metodos para listar, baixar, deletar e atualizar templates por cluster.
 */

import { apiClient } from './api'
import type { VMTemplateDownloadResponse } from './vmTemplatesApi'

// ===== Types =====

export type ClusterTemplate = {
  id: string
  cluster_id: string
  vm_template_id: string
  status: 'downloading' | 'ready' | 'error' | 'deleting' | 'unknown' | 'missing'
  proxmox_vmid: number | null
  proxmox_node: string | null
  storage_pool: string | null
  /** null = indeterminado; false prende o provisionamento ao proxmox_node */
  storage_is_shared: boolean | null
  disk_size_bytes: number | null
  synced_version: string | null
  error_message: string | null
  created_at: string
  updated_at: string
  last_synced_at: string | null

  /** catalog = baixado do catálogo Nexxus; cluster_import = já existia no Proxmox do cliente */
  source: 'catalog' | 'cluster_import' | null

  // Dados do VMTemplate (join)
  template_name: string | null
  template_version: string | null
  template_category: string | null
  template_format: string | null
  template_os_type: string | null
  template_os_variant: string | null
  template_supports_cloud_init: boolean | null
}

export type ClusterTemplateListResponse = {
  templates: ClusterTemplate[]
  total: number
}

/** Metadados confirmados pelo CP ao registrar um template próprio. */
export type RegisterTemplatePayload = {
  vmid: number
  name: string
  category: string
  version?: string
  os_type?: string | null
  os_variant?: string | null
  os_version?: string | null
  description?: string | null
}

/** Resultado do reparo do CloudInit Drive num template do Proxmox. */
export type AddCloudInitDriveResponse = {
  vmid: number
  node: string
  /** false = o template ja tinha o drive; nada foi escrito no Proxmox */
  changed: boolean
  has_cloud_init: boolean
  /** Barramento onde o drive foi criado (`ide2` na convencao); null quando ja existia */
  drive: string | null
  /** Storage do disco do template, herdado pelo drive; null quando ja existia */
  storage: string | null
}

// ===== API Functions =====

const clusterTemplateService = {
  /**
   * Lista todos os templates de um cluster
   */
  getClusterTemplates: (clusterId: string): Promise<ClusterTemplateListResponse> => {
    return apiClient.get(`/api/v1/clusters/${clusterId}/templates`)
  },

  /**
   * Inicia download de um template para um cluster
   */
  downloadTemplate: (
    clusterId: string,
    templateId: string,
  ): Promise<VMTemplateDownloadResponse> => {
    return apiClient.post(`/api/v1/clusters/${clusterId}/templates/${templateId}/download`)
  },

  /**
   * Remove tracking de um template de um cluster
   */
  deleteClusterTemplate: (clusterId: string, templateId: string): Promise<void> => {
    return apiClient.delete(`/api/v1/clusters/${clusterId}/templates/${templateId}`)
  },

  /**
   * Atualiza versao de um template em um cluster (re-download).
   * Indisponivel para template importado do cluster — use `reinspectTemplate`.
   */
  updateClusterTemplate: (
    clusterId: string,
    templateId: string,
  ): Promise<VMTemplateDownloadResponse> => {
    return apiClient.post(`/api/v1/clusters/${clusterId}/templates/${templateId}/update`)
  },

  /**
   * Registra um template que ja existe no Proxmox, promovendo-o ao catalogo da org.
   * Os metadados vao confirmados pelo CP: o `ostype` do Proxmox nao identifica o SO real.
   */
  registerFromProxmox: (
    clusterId: string,
    payload: RegisterTemplatePayload,
  ): Promise<ClusterTemplate> => {
    return apiClient.post(`/api/v1/clusters/${clusterId}/templates/register`, payload)
  },

  /**
   * Rele a config do template no Proxmox e atualiza os metadados (so para importados).
   */
  reinspectTemplate: (clusterId: string, vmTemplateId: string): Promise<ClusterTemplate> => {
    return apiClient.post(`/api/v1/clusters/${clusterId}/templates/${vmTemplateId}/reinspect`)
  },

  /**
   * Adiciona o CloudInit Drive a um VM template que esta no Proxmox sem ele.
   *
   * Reparo dos templates publicados antes da #744: o pacote publicado e' so a imagem de
   * disco, e o importer do agent remontava a VM sem `ide2`. Idempotente — template que ja
   * tem o drive volta com `changed: false`.
   */
  addCloudInitDrive: (clusterId: string, vmid: number): Promise<AddCloudInitDriveResponse> => {
    return apiClient.post(
      `/api/v1/clusters/${clusterId}/templates/proxmox/${vmid}/cloud-init-drive`,
    )
  },
}

// ===== Helper Functions =====

/**
 * Retorna cor do badge de status
 */
export const getClusterTemplateStatusColor = (status: ClusterTemplate['status']): string => {
  const colors: Record<ClusterTemplate['status'], string> = {
    downloading: 'bg-yellow-100 text-yellow-800',
    ready: 'bg-green-100 text-green-800',
    error: 'bg-red-100 text-red-800',
    deleting: 'bg-orange-100 text-orange-800',
    unknown: 'bg-gray-100 text-gray-800',
    missing: 'bg-red-100 text-red-800',
  }
  return colors[status] || 'bg-gray-100 text-gray-800'
}

/**
 * Retorna label legivel do status
 */
export const getClusterTemplateStatusLabel = (status: ClusterTemplate['status']): string => {
  const labels: Record<ClusterTemplate['status'], string> = {
    downloading: 'Baixando',
    ready: 'Disponível',
    error: 'Erro',
    deleting: 'Removendo',
    unknown: 'Desconhecido',
    missing: 'Sumiu do Proxmox',
  }
  return labels[status] || status
}

export default clusterTemplateService
